<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { useBottomNavStore } from '@/stores/bottomNavStore'
import { useWeekStore } from '@/stores/week'
import { useAuthStore } from '@/stores/authStore'
import { useRouter } from 'vue-router'
import PWAInstallPrompt from '@/components/PWAInstallPrompt.vue'
import BottomNavigation from '@/components/BottomNavigation.vue'
import LoadingModal from '@/components/LoadingModal.vue'
import AlertModal from '@/components/AlertModal.vue'
import { api, brandDenied } from '@/services/api'
import { useNotificationsStore } from '@/stores/notifications'
import { useTheme } from '@/composables/useTheme'
import QrVerificationToast from '@/components/QrVerificationToast.vue'
import { startQrKeyRefresh, stopQrKeyRefresh, refreshQrVerification } from '@/services/qrKeyCache.js'
import PushReachabilityBanner from '@/components/PushReachabilityBanner.vue'
import { usePushReachabilityStore } from '@/stores/pushReachability.js'
import { useInboxStore } from '@/stores/inbox.js'

const apiUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.snowfuze.com'
const isNonProduction = apiUrl !== 'https://api.snowfuze.com'

const { initTheme } = useTheme()
const bottomNavStore = useBottomNavStore()
const weekStore = useWeekStore()
const authStore = useAuthStore()
const router = useRouter()
const notificationsStore = useNotificationsStore()
const pushReachability = usePushReachabilityStore()
const inbox = useInboxStore()

// Global notification modal state (sits on top of everything)
const showNotification = ref(false)
const notificationTitle = ref('SnowFuze')
const notificationMessage = ref('You have a new notification.')
const notificationMessageId = ref(null)

function openNotificationModal({ title = 'SnowFuze', body = 'You have a new notification.', messageId = null } = {}) {
  notificationTitle.value = title
  notificationMessage.value = body
  notificationMessageId.value = messageId
  showNotification.value = true
  // Save to local history
  try { notificationsStore.addMessage({ title, body, messageId, timestamp: Date.now() }) } catch {}
  // Best-effort received logging; ignore failures/unauthed
  if (messageId) {
    api.post('/api/subscriptions/received', { messageId }).catch(() => {})
  }
}

function handleSwMessage(event) {
  const data = event?.data || {}
  if (!data) return
  if (data.type === 'SHOW_NOTIFICATION_MODAL') {
    const payload = data.payload || {}
    openNotificationModal({ title: payload.title || 'SnowFuze', body: payload.body, messageId: payload.messageId })
  } else if (data.type === 'STORE_NOTIFICATION') {
    const payload = data.payload || {}
    try { notificationsStore.addMessage({ title: payload.title || 'SnowFuze', body: payload.body || '', messageId: payload.messageId, timestamp: payload.timestamp || Date.now() }) } catch {}
  } else if (data.type === 'INBOX_NOTIFICATION') {
    // notifications-v2 (#141): the server-side inbox replaces the local history and the modal
    inbox.arrived({ unread: data.payload?.unread ?? null })
  } else if (data.type === 'OPEN_INBOX') {
    // A tapped push with the app already open: route in place rather than reload
    const url = data.payload?.url
    if (typeof url === 'string' && url.startsWith('/inbox')) router.push(url).catch(() => {})
  }
}

// Back in the foreground: pushes may have arrived, or been read or acknowledged on another device
// (the acknowledgement banner goes once a notification is closed for this staff member, #142)
function handleVisibility() {
  if (document.visibilityState === 'visible' && api.refreshToken && inbox.enabled) inbox.refresh()
}

const handleLogout = () => {
  weekStore.isLoading = false;
  weekStore.error = null;
  authStore.logOut()
  router.push('/login')
}

// A valid session for the wrong brand. Dismissing leaves them signed in, because
// the usual cause is opening another brand's URL by mistake rather than anything
// being wrong with the account - but logging out is one tap away for a rep who
// genuinely needs to sign in as someone else.
const handleBrandDeniedLogout = () => {
  brandDenied.value = null
  handleLogout()
}

// The week index the QR key cache syncs for. weekStore is empty until the week
// data reloads, so fall back to the week persisted at selection.
const currentWeekId = () => {
  if (weekStore.weekId) return weekStore.weekId
  try {
    return JSON.parse(localStorage.getItem('weekId') || 'null')?.weekId ?? null
  } catch {
    return null
  }
}

// Login and week selection both land here: pull that week's QR signing keys.
watch(() => weekStore.weekId, (weekId) => {
  if (weekId) refreshQrVerification(weekId)
})

onMounted(() => {
  initTheme()
  startQrKeyRefresh(currentWeekId)
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', handleSwMessage)
  }
  // App open with a session: refresh this push device and its reachability
  // (notifications-v2 only). Login does the same from LoginPage.
  if (api.refreshToken) {
    pushReachability.refresh()
    // The inbox, bell and app badge (#141, notifications-v2 only)
    inbox.refresh()
  }
  document.addEventListener('visibilitychange', handleVisibility)
  // Support deep link that always shows the modal regardless of route guards
  if (window.location.hash === '#notifications') {
    openNotificationModal({})
  }
})

onBeforeUnmount(() => {
  stopQrKeyRefresh()
  document.removeEventListener('visibilitychange', handleVisibility)
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.removeEventListener('message', handleSwMessage)
  }
})
</script>

<template>
  <div class="app-container" :class="{ 'non-production': isNonProduction }">
    <PushReachabilityBanner />
    <RouterView />
    <BottomNavigation v-if="bottomNavStore.showBottomNav && weekStore.weekId" />
    <PWAInstallPrompt />
    <QrVerificationToast />
    <LoadingModal
        :isLoading="weekStore.isLoading"
        :error="weekStore.error"
        @logout="handleLogout"
    />
    <AlertModal
        :show="showNotification"
        :title="notificationTitle"
        :message="notificationMessage"
        type="info"
        confirm-text="OK"
        :show-cancel="false"
        @close="showNotification = false"
        @confirm="showNotification = false"
    />

    <AlertModal
        :show="!!brandDenied"
        title="Wrong app for this account"
        :message="brandDenied?.message || 'You do not have access to this brand.'"
        type="warning"
        confirm-text="Log out"
        cancel-text="Stay signed in"
        :show-cancel="true"
        @close="brandDenied = null"
        @confirm="handleBrandDeniedLogout"
    />
  </div>
</template>

<style lang="scss">
@use '@/styles/main';

.app-container {
  padding-bottom: 60px;
  background: var(--tblr-body-bg);

  &.non-production {
    --tblr-primary: #e67e22;
    --tblr-primary-rgb: 230, 126, 34;
  }
}

.card-status-top {
  padding: 2px 0px;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: transform 0.3s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(100%);
}

// Accordion styling overrides for Tabler 1.4.0
.accordion-button {
  font-size: 0.875rem;
  padding: 0.75rem 1rem;

  // Hide Bootstrap's default chevron
  &::after {
    display: none;
  }
}

.accordion-header {
  margin-bottom: 0;
}

.accordion-body {
  padding: 0.75rem 1rem;
}

// Chevron toggle styling
.accordion-button-toggle {
  display: flex;
  align-items: center;
  transition: transform 0.2s ease-in-out;

  .accordion-button:not(.collapsed) & {
    transform: rotate(180deg);
  }
}
</style>
